import React, { useState, useEffect } from 'react';
import './ExpensesManager.css';

const ExpensesManager = () => {
  const [expenses, setExpenses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [formData, setFormData] = useState({
    id: '',
    description: '',
    amount: 0,
    category: '',
    date: '',
    vendor: '',
    paymentMethod: '',
    receiptUrl: '',
    status: 'pending',
    notes: ''
  });

  const categories = [
    'Office Supplies',
    'Software & Tools',
    'Marketing & Advertising',
    'Travel & Transportation',
    'Meals & Entertainment',
    'Professional Services',
    'Equipment & Hardware',
    'Utilities & Rent',
    'Insurance',
    'Other'
  ];

  const paymentMethods = [
    'Credit Card',
    'Debit Card',
    'Cash',
    'Bank Transfer',
    'Check',
    'PayPal',
    'Other'
  ];

  useEffect(() => {
    loadExpenses();
  }, []);

  const loadExpenses = () => {
    const saved = localStorage.getItem('expenses');
    if (saved) {
      setExpenses(JSON.parse(saved));
    }
  };

  const saveExpenses = (expensesData) => {
    localStorage.setItem('expenses', JSON.stringify(expensesData));
    setExpenses(expensesData);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newExpenses = [...expenses];
    
    if (editingExpense) {
      const index = newExpenses.findIndex(e => e.id === editingExpense.id);
      newExpenses[index] = { ...formData };
    } else {
      const newExpense = {
        ...formData,
        id: Date.now().toString(),
        createdAt: new Date().toISOString()
      };
      newExpenses.push(newExpense);
    }
    
    saveExpenses(newExpenses);
    resetForm();
    setShowModal(false);
  };

  const resetForm = () => {
    setFormData({
      id: '',
      description: '',
      amount: 0,
      category: '',
      date: '',
      vendor: '',
      paymentMethod: '',
      receiptUrl: '',
      status: 'pending',
      notes: ''
    });
    setEditingExpense(null);
  };

  const handleEdit = (expense) => {
    setFormData(expense);
    setEditingExpense(expense);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      const newExpenses = expenses.filter(e => e.id !== id);
      saveExpenses(newExpenses);
    }
  };

  const calculateTotalExpenses = () => {
    return expenses.reduce((sum, expense) => sum + expense.amount, 0);
  };

  const calculateMonthlyTotal = () => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    return expenses
      .filter(expense => {
        const expenseDate = new Date(expense.date);
        return expenseDate.getMonth() === currentMonth && expenseDate.getFullYear() === currentYear;
      })
      .reduce((sum, expense) => sum + expense.amount, 0);
  };

  const getExpensesByStatus = (status) => {
    return expenses.filter(expense => expense.status === status).length;
  };

  return (
    <div className="expenses-manager">
      <div className="expenses-header">
        <h2>Expenses Management</h2>
        <button onClick={() => setShowModal(true)} className="add-expense-btn">
          + Add New Expense
        </button>
      </div>

      <div className="expenses-summary">
        <div className="summary-card">
          <h3>Total Expenses</h3>
          <p className="summary-amount">${calculateTotalExpenses().toFixed(2)}</p>
        </div>
        <div className="summary-card">
          <h3>This Month</h3>
          <p className="summary-amount">${calculateMonthlyTotal().toFixed(2)}</p>
        </div>
        <div className="summary-card">
          <h3>Pending</h3>
          <p className="summary-amount">{getExpensesByStatus('pending')}</p>
        </div>
        <div className="summary-card">
          <h3>Approved</h3>
          <p className="summary-amount">{getExpensesByStatus('approved')}</p>
        </div>
      </div>

      <div className="expenses-grid">
        {expenses.map(expense => (
          <div key={expense.id} className="expense-card">
            <div className="expense-header">
              <h3>{expense.description}</h3>
              <span className={`status ${expense.status}`}>{expense.status}</span>
            </div>
            <div className="expense-details">
              <p><strong>Amount:</strong> ${expense.amount.toFixed(2)}</p>
              <p><strong>Category:</strong> {expense.category}</p>
              <p><strong>Date:</strong> {expense.date}</p>
              <p><strong>Vendor:</strong> {expense.vendor}</p>
              <p><strong>Payment:</strong> {expense.paymentMethod}</p>
            </div>
            {expense.notes && (
              <div className="expense-notes">
                <p><strong>Notes:</strong> {expense.notes}</p>
              </div>
            )}
            <div className="expense-actions">
              <button onClick={() => handleEdit(expense)} className="edit-btn">Edit</button>
              <button onClick={() => handleDelete(expense.id)} className="delete-btn">Delete</button>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingExpense ? 'Edit Expense' : 'Add New Expense'}</h3>
              <button onClick={() => { setShowModal(false); resetForm(); }} className="close-btn">×</button>
            </div>
            
            <form onSubmit={handleSubmit} className="expense-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Description *</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Amount *</label>
                  <input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) })}
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Vendor</label>
                  <input
                    type="text"
                    value={formData.vendor}
                    onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Payment Method</label>
                  <select
                    value={formData.paymentMethod}
                    onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                  >
                    <option value="">Select Payment Method</option>
                    {paymentMethods.map(method => (
                      <option key={method} value={method}>{method}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Receipt URL</label>
                <input
                  type="url"
                  value={formData.receiptUrl}
                  onChange={(e) => setFormData({ ...formData, receiptUrl: e.target.value })}
                  placeholder="https://example.com/receipt"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="reimbursed">Reimbursed</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows="3"
                  placeholder="Additional notes about this expense..."
                />
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="cancel-btn">
                  Cancel
                </button>
                <button type="submit" className="save-btn">
                  {editingExpense ? 'Update Expense' : 'Add Expense'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensesManager; 